/**
 * Repository Analysis API
 *
 * Functions for triggering and fetching AI-generated architecture summaries.
 */

import { api } from "./client";

export interface FocusArea {
  name: string;
  description: string;
  evidence?: {
    commits?: string[];
    pull_requests?: string[];
  };
}

export interface Contributor {
  name: string;
  active_areas?: string[];
}

export interface StaleItem {
  type: string;
  title: string;
  last_activity: string;
  reason: string;
}

export interface ActiveWork {
  focus_areas?: FocusArea[];
  contributors?: Contributor[];
  stale_items?: StaleItem[];
}

export interface RepoAnalysis {
  id: string;
  repo_link_id: string;
  project_id: string;
  status: "pending" | "running" | "complete" | "failed";
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  current_version_id: string | null;
  last_commit_sha: string | null;
  analyzed_at: string | null;
  ai_summary_cache: string | null;
  active_work: ActiveWork | null;
  created_at: string;
  updated_at: string;
}

export interface RepoAnalysisVersion {
  id: string;
  analysis_id: string;
  version_source: "ai_generated" | "user_edit";
  created_by: string | null;
  architecture_summary: string;
  repo_metadata: Record<string, unknown>;
  created_at: string;
}

export interface RepoBusinessInference {
  id: string;
  analysis_id: string;
  inferred_purpose: string;
  purpose_confidence: "high" | "medium" | "low";
  target_audience: string | null;
  audience_confidence: "high" | "medium" | "low";
  inferred_objectives: Array<{ text: string; confidence: string }>;
  domain_terms: Array<{ term: string; confidence: string }>;
  accepted_fields: string[];
  created_at: string;
  updated_at: string;
}

export interface RepoComponent {
  id: string;
  analysis_id: string;
  module_name: string;
  file_path: string;
  language: string | null;
  line_count: number | null;
  complexity: "low" | "medium" | "high" | null;
  has_tests: boolean;
  created_at: string;
}

export interface RepoDebtFlag {
  id: string;
  analysis_id: string;
  debt_type:
    | "missing_tests"
    | "outdated_dependency"
    | "security_risk"
    | "complex_function"
    | "missing_docs"
    | "deprecated_api"
    | "other";
  severity: "low" | "medium" | "high" | "critical";
  location: string | null;
  description: string;
  dismissed: boolean;
  dismissed_by: string | null;
  dismissed_at: string | null;
  created_at: string;
}

export interface RepoAnalysisSummaryResponse {
  analysis: RepoAnalysis;
  current_version: RepoAnalysisVersion | null;
  has_user_edits: boolean;
  business_inference: RepoBusinessInference | null;
  components: RepoComponent[];
  debt_flags: RepoDebtFlag[];
}

export async function triggerAnalysis(
  projectId: string,
  repoId: string,
): Promise<RepoAnalysis> {
  return api.post<RepoAnalysis>(
    `/projects/${projectId}/github/repos/${repoId}/analyze`,
    {},
  );
}

export async function getAnalysis(
  projectId: string,
  repoId: string,
): Promise<RepoAnalysisSummaryResponse> {
  return api.get<RepoAnalysisSummaryResponse>(
    `/projects/${projectId}/github/repos/${repoId}/analysis`,
  );
}

export async function updateArchitectureSummary(
  projectId: string,
  repoId: string,
  architectureSummary: string,
): Promise<RepoAnalysisSummaryResponse> {
  return api.put<RepoAnalysisSummaryResponse>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/summary`,
    { architecture_summary: architectureSummary },
  );
}

export async function resetArchitectureSummary(
  projectId: string,
  repoId: string,
): Promise<RepoAnalysisSummaryResponse> {
  return api.post<RepoAnalysisSummaryResponse>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/summary/reset`,
    {},
  );
}

export async function getAnalysisVersions(
  projectId: string,
  repoId: string,
): Promise<RepoAnalysisVersion[]> {
  return api.get<RepoAnalysisVersion[]>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/versions`,
  );
}

export async function acceptBusinessInference(
  projectId: string,
  repoId: string,
  fields: string[],
): Promise<RepoBusinessInference> {
  return api.post<RepoBusinessInference>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/business-context/accept`,
    { fields },
  );
}

export async function getDebtFlags(
  projectId: string,
  repoId: string,
): Promise<RepoDebtFlag[]> {
  return api.get<RepoDebtFlag[]>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/debt-flags`,
  );
}

export async function dismissDebtFlag(
  projectId: string,
  repoId: string,
  flagId: string,
): Promise<RepoDebtFlag> {
  return api.post<RepoDebtFlag>(
    `/projects/${projectId}/github/repos/${repoId}/analysis/debt-flags/${flagId}/dismiss`,
    {},
  );
}
